/*
 * Copyright Thoughtworks, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import _ from "lodash";
import {MaterialType} from "../models/materials/materials";

export type CommentServerFormat = 'html' | 'json' | 'raw';

export interface TrackingTool {
  regex: string;
  link: string;
}

// See legacy JS render at (app/assets/javascripts/vsm_renderer.js)
export function renderCommentToHtml(text: string, textType: CommentServerFormat, trackingTool?: TrackingTool) {
  switch (textType) {
    case "html":
      // Assume it is already escaped and safe to render directly as trusted
      return text;
    case "json":
      // We need to extract comment from json; then make it safe, possibly with tracking tool links
      return renderRawCommentToHtml(parseJsonCommentUnsafe(text), trackingTool);
    case "raw":
      // The default case; assume it is unsafe from server. If this is passed incorrectly, it'll lead to
      // double-escaping and bad rendering.
      return renderRawCommentToHtml(text, trackingTool);
  }
}

// Renders to safe HTML, assuming input is raw or json. Case-insensitive on materialType, since callers pass both
// the config type ("package") and the display type ("Package").
export function renderCommentToHtmlByType(text: string, materialType: MaterialType | string) {
  return renderCommentToHtml(text, materialType?.toLowerCase() === "package" ? "json" : "raw");
}

// Parses server comments assuming they are being returned raw. Does not make changes that affect style of rendering
// but does adapt the comment by material type.
export function parseRawCommentUnsafe(text: string, materialType: MaterialType) {
  return materialType === "package" ? parseJsonCommentUnsafe(text) : text;
}

// Extracts a package-material JSON comment envelope into human-readable *unescaped* plain text
// (COMMENT + trackback). Callers must escape the result before inserting as HTML (e.g. render it as mithril text,
// or pass it through renderRawCommentToHtml). Does not render the trackback as a link at this stage (unlike the legacy
// Rails and sprockets raw JS code).
function parseJsonCommentUnsafe(text: string) {
  try {
    const commentJSON   = JSON.parse(text);
    const trackbackURL  = commentJSON?.TRACKBACK_URL || "Not Provided";
    const packageOrigin = _.isEmpty(commentJSON.COMMENT) ? "" : `${commentJSON.COMMENT}\n`;
    return `${packageOrigin}Trackback: ${trackbackURL}`;
  } catch (e) {
    return text;
  }
}

function renderRawCommentToHtml(text: string, trackingTool?: TrackingTool) {
  if (!trackingTool || !trackingTool.regex) {
    return _.escape(text);
  } else {
    return renderRawCommentWithTrackingToolToHtml(text, trackingTool);
  }
}

function renderRawCommentWithTrackingToolToHtml(text: string, trackingTool: TrackingTool) {
  try {
    const regex              = new RegExp(trackingTool.regex);
    const linkIdFromGroup    = regexHasGroups();
    const commentStringParts = [];
    let matchResult          = text.match(regex);
    while (matchResult !== null) {
      commentStringParts.push(_.escape(text.substring(0, matchResult.index)));
      commentStringParts.push(toLink(matchResult, linkIdFromGroup));
      text        = text.substring(matchResult.index! + matchResult[0].length);
      matchResult = text.match(regex);
    }
    commentStringParts.push(_.escape(text));
    return commentStringParts.join("");
  } catch (e) {
    return _.escape(text);
  }

  function regexHasGroups() {
    return (new RegExp(`${trackingTool!.regex}|`)).exec("")!.length - 1 !== 0;
  }

  function toLink(matchResult: RegExpMatchArray, linkIdFromGroup: boolean) {
    const matchedWord = matchResult[0];
    const trackingId = firstMatchingGroup(matchResult);
    if (trackingId || !linkIdFromGroup) {
      const href = trackingTool!.link.replace("${ID}", encodeURIComponent(trackingId || matchedWord));
      return `<a href="${_.escape(href)}" target="story_tracker">${_.escape(matchedWord)}</a>`;
    } else {
      return _.escape(matchedWord);
    }
  }

  function firstMatchingGroup(matchResult: RegExpMatchArray) {
    for (let i = 1; i < matchResult.length; i++) {
      if (matchResult[i]) {
        return matchResult[i];
      }
    }
    return null;
  }
}
